import React, { useState, useEffect } from 'react';
import AppButtonGroup from 'components/main/AppButtons';
import { Stack, XStack, YStack, Heading, SizableText } from 'tamagui';
import { Flex, Center } from 'components/base/General';
import { useGetBlogsShortQuery } from 'utils/RTKGraphQL';
import { truncateStr } from 'utils/misc';
import {
  FacebookIcon,
  TwitterIcon,
  InstagramIcon,
} from 'app/components/utility/SVGIcons';
import { Link } from 'components/base/General';
import Loading from 'components/utility/Loading';

interface HoverBoxProps {
  label: string;
  isExternal?: boolean;
  to: string;
  children: React.ReactNode;
}

const HoverBox = (props: HoverBoxProps) => {
  return (
    <Link
      display="flex"
      accessibilityLabel={props.label}
      justifyContent="center"
      alignItems="center"
      isExternal={props.isExternal ? props.isExternal : false}
      mb={15}
      href={props.to}
      bg="$blueGray800"
      height={50}
      width={50}
      hoverStyle={{
        backgroundColor: '$pred500',
      }}
    >
      {props.children}
    </Link>
  );
};

export const SocialMedia = () => {
  return (
    <XStack space={5}>
      <HoverBox
        isExternal={true}
        to="https://www.facebook.com/thepandaist"
        label="Facebook"
      >
        <FacebookIcon size={15} color="#fff" />
      </HoverBox>
      <HoverBox
        isExternal={true}
        to="https://twitter.com/pandaist_com"
        label="Twitter"
      >
        <TwitterIcon size={15} color="#fff" />
      </HoverBox>
      <HoverBox
        isExternal={true}
        to="https://www.instagram.com/thepandaist"
        label="Instagram"
      >
        <InstagramIcon size={15} color="#fff" />
      </HoverBox>
    </XStack>
  );
};

const fontFamily = 'Poppins, sans-serif';

interface HoverLinkProps {
  isExternal?: boolean;
  to: string;
  fontSize?: string;
  children: React.ReactNode;
}

const HoverLink = (props: HoverLinkProps) => {
  return (
    <Link
      isExternal={props.isExternal ? props.isExternal : false}
      color={'$coolGray500'}
      fontFamily={fontFamily}
      mb={15}
      href={props.to}
      hoverStyle={{
        color: '#FFF',
      }}
      fontSize={props.fontSize ? props.fontSize : '$sm'}
    >
      {props.children}
    </Link>
  );
};

interface FooterHeadingProps {
  url?: string;
  heading: string;
}

const FooterHeading = (props: FooterHeadingProps) => {
  return (
    <>
      {props.url ? (
        <Link
          href={props.url}
          hoverStyle={{ textDecoration: 'none', opacity: 0.5 }}
          justifyContent="center"
          textAlign="center"
          mb={35}
        >
          <Heading color="white" mb={35} textAlign="center" fontSize="$xl">
            {props.heading}
          </Heading>
        </Link>
      ) : (
        <Heading color="white" mb={35} textAlign="center" fontSize="$xl">
          {props.heading}
        </Heading>
      )}
    </>
  );
};

interface FooterColumnProps {
  heading?: string;
  url?: string;
  children: React.ReactNode;
}

const FooterColumn = (props: FooterColumnProps) => {
  return (
    <YStack>
      {props.heading ? (
        <FooterHeading heading={props.heading} url={props.url} />
      ) : null}
      <Center>{props.children}</Center>
    </YStack>
  );
};

const Resources = () => {
  return (
    <FooterColumn heading="Resources">
      <HoverLink to="/hsk-charts/">HSK Charts</HoverLink>
      <HoverLink to="/pinyin-chart/">Pinyin Chart</HoverLink>
      <HoverLink
        isExternal={true}
        to="https://resources.allsetlearning.com/chinese/grammar/Main_Page"
      >
        Chinese Grammar Wiki
      </HoverLink>
      <HoverLink isExternal={true} to="https://apps.ankiweb.net/">
        Anki Flashcards
      </HoverLink>
      <HoverLink
        isExternal={true}
        to="https://ai.glossika.com/r/fi3m/learn-chinese-beijing"
      >
        Glossika
      </HoverLink>
      <HoverLink to="/terms/">Terms</HoverLink>
      <HoverLink to="/privacy/">Privacy</HoverLink>
    </FooterColumn>
  );
};

const Blogs = () => {
  const {
    data: blogsData,
    isLoading,
    error,
  } = useGetBlogsShortQuery({
    page: 1,
    pageSize: 4,
    published: true,
    ordering: '-date',
  });

  if (isLoading)
    return (
      <FooterColumn heading="Blog">
        <Loading />
      </FooterColumn>
    );

  if (error) {
    return (
      <FooterColumn heading="Blog">
        <Stack>
          <SizableText color="white">Error loading blogs</SizableText>
        </Stack>
      </FooterColumn>
    );
  }

  const blogs = blogsData?.results || [];

  return (
    <FooterColumn heading="Blog" url="/blog/">
      {blogs.map((blog, index) => (
        <HoverLink key={index} to={`/blog/${blog.langCode}/${blog.slug}`}>
          {truncateStr(blog.title, 40, true)}
        </HoverLink>
      ))}
    </FooterColumn>
  );
};

const AppButtonCol = () => {
  return (
    <FooterColumn>
      <AppButtonGroup
        flexDirection={'column'}
        alignItems={'center'}
        justifyContent={'space-between'}
      />
    </FooterColumn>
  );
};

const CopyrightText = () => {
  return (
    <SizableText color="white" fontFamily={fontFamily} fontSize="$xs">
      Copyright &copy;{' '}
      <HoverLink isExternal={true} to="https://codexr.io/" fontSize="$xs">
        Codexr, LLC
      </HoverLink>
      {' ' + new Date().getFullYear().toString() + ' '}
      All rights reserved
    </SizableText>
  );
};

const Footer = () => {
  return (
    <Center bg="black" py={75}>
      <Stack
        space={25}
        bg="black"
        justifyContent="space-around"
        mb={100}
        width="100%"
        flexDirection="column"
        $sm={{ width: '95%', flexDirection: 'column' }}
        $md={{ width: '85%', flexDirection: 'row' }}
        $lg={{ width: 900, flexDirection: 'row' }}
        $xl={{ width: 1200, flexDirection: 'row' }}
      >
        <AppButtonCol />
        <Resources />
        <Blogs />
      </Stack>
      <SocialMedia />
      <Flex pt={100}>
        <CopyrightText />
      </Flex>
    </Center>
  );
};

export default Footer;
